// Billing hub (/office/billing, Session ATB Phase 4, D30: billing area consolidation, M-5).
// Before this page, "money" was five unlinked pages the office had to hunt across (Invoices, Aged
// debtors, PPM payments, Allowances, plus the retired Invoices due). This page is the one billing-
// shaped home the audit asked for: a card per area, each with a live headline figure where a cheap
// existing endpoint already gives one, and a link through to that area's own full page. Nothing here
// is a new aggregate or a new business rule -- every figure is read from the same endpoint that area's
// own page already reads (RULE 6: derive live, do not cache and hope), and the FOUR underlying pages
// are untouched and keep their own routes, filters and sort. The old "Invoices due" batch/sweep
// (billing.routes.ts's invoices-due endpoints) was disabled at Session AKF and removed at Session EXP1:
// nothing here resurrects it.
//
// The Xero billing check card (Session ATG) gained the same out-of-step figure the dashboard's own
// XeroBillingTile shows, read from the SAME GET /api/dashboard call through the SAME pure
// xeroBillingTileContent function -- this card and the dashboard tile can never disagree.
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../../lib/auth";
import { api, apiRequest } from "../../lib/api";
import type { AgedDebtorReport, ContractAllowanceRow, DashboardTiles, PlatformCountsLite } from "../../lib/api";
import { PageHeader } from "../../components/PageHeader";
import { Loader } from "../../components/Loader";
import { xeroBillingTileContent } from "./OfficeDashboard";

interface PpmPaymentsSummary {
  outstanding: number;
}

// A card's headline is a small, self-contained fetch: one area failing to load must never block the
// others, and a card with no live figure yet (still loading) reads as a quiet placeholder rather than
// a blocking spinner for the whole hub.
function useCount<T>(load: () => Promise<T>): { value: T | null; error: boolean } {
  const [value, setValue] = useState<T | null>(null);
  const [error, setError] = useState(false);
  useEffect(() => {
    let live = true;
    load()
      .then((v) => {
        if (live) setValue(v);
      })
      .catch(() => {
        if (live) setError(true);
      });
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return { value, error };
}

function BillingCard({
  to,
  title,
  description,
  figure,
  figureLabel,
  tone = "neutral",
  error,
}: {
  to: string;
  title: string;
  description: string;
  figure: string | null;
  figureLabel: string;
  tone?: "neutral" | "amber" | "red";
  error?: boolean;
}) {
  // The red band is the fixed #c0392b through --ele-danger-fill (white ink 5.44:1 in both themes);
  // the amber band keeps dark ink on amber (no amber fill token exists, as on the Compliance page).
  const BAND: Record<"neutral" | "amber" | "red", string> = {
    red: "bg-ele-danger-fill text-white",
    amber: "bg-amber-400 text-black",
    neutral: "bg-ele-inset-strong text-ele-strong",
  };
  const band = BAND[tone];
  return (
    <Link to={to} className="block overflow-hidden rounded-ele border border-ele-purple-accent transition hover:border-ele-green">
      <div className={`flex items-baseline justify-between px-4 py-3 ${band}`}>
        <span className="font-heading text-lg tracking-wide">{title}</span>
        {error ? (
          <span className="text-xs opacity-70">Could not load</span>
        ) : figure === null ? (
          <Loader shape="inline" className="opacity-70" />
        ) : (
          <span className="text-right">
            <span className="block font-heading text-2xl leading-none">{figure}</span>
            <span className="block text-[10px] uppercase tracking-widest opacity-80">{figureLabel}</span>
          </span>
        )}
      </div>
      <p className="px-4 py-3 text-xs text-ele-body">{description}</p>
    </Link>
  );
}

function moneyGBP(pence: number): string {
  return `£${(pence / 100).toLocaleString("en-GB", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}

export function BillingHubPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  // Session SC2B: the Invoices card prints a COUNT, so it reads the count (GET /api/reporting/counts), not the 5,912 invoices at 1,000 lifts.
  const invoices = useCount<PlatformCountsLite>(() => api.reporting.counts());
  const debtors = useCount<AgedDebtorReport>(() => api.invoices.agedDebtors());
  const allowances = useCount<ContractAllowanceRow[]>(() => api.contracts.allowances());
  const ppmPayments = useCount<PpmPaymentsSummary>(async () => {
    const data = await apiRequest<{ visits: { jobStatus: string; scheduledFor: string; paymentState: string }[]; runwayDays?: number }>(
      "/scheduling/ppm-payments",
    );
    // Mirrors PpmPaymentsPage's own "needs attention" filter: a visit whose PPM invoice has not
    // been raised, read the same way that page's own outline count is read.
    return { outstanding: data.visits.filter((v) => v.paymentState !== "paid" && v.paymentState !== "invoiced").length };
  });
  // Session ATG: the SAME dashboard read the office dashboard's own XeroBillingTile uses
  // (GET /api/dashboard, tiles.xeroBilling), through the SAME pure content function
  // (xeroBillingTileContent), so this card can never disagree with the dashboard tile -- RULE 6,
  // nothing here re-derives the out-of-step count.
  const dashboardTiles = useCount<DashboardTiles>(() => api.dashboard.get());
  const xeroBilling = dashboardTiles.value?.xeroBilling ?? null;
  const xeroContent = xeroBilling ? xeroBillingTileContent(xeroBilling) : null;
  const xeroNotChecked = xeroBilling ? xeroBilling.stale || xeroBilling.checkedAt == null : false;
  const xeroTone: "neutral" | "amber" | "red" = xeroNotChecked ? "amber" : xeroBilling && xeroBilling.outOfStep > 0 ? "red" : "neutral";
  // A loaded response with no `xeroBilling` field at all (an older server) must not read as "still
  // loading" for ever: it falls back to a dash, matching the card's own "nothing to show" idiom.
  const xeroFigure = xeroContent ? xeroContent.value : dashboardTiles.value != null ? "n/a" : null;

  return (
    <div>
      <PageHeader
        className="mb-5"
        title="Billing"
        subtitle="One home for the money side of the business: what is owed, what is overdue, what a contract's inclusive allowance has left, and whether Xero's own recurring invoices still match what each contract is meant to bill. Every figure here is read live from the page it links to."
      />

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        <BillingCard
          to="/office/invoices"
          title="Invoices"
          description="Every Xero invoice the platform has cached, draft or authorised. Search, filter and open any one in Xero."
          figure={invoices.value ? String(invoices.value.invoices.tracked) : null}
          figureLabel="tracked"
          error={invoices.error}
        />
        <BillingCard
          to="/office/debtors"
          title="Aged debtors"
          description="Outstanding invoices by how overdue they are, oldest first."
          figure={debtors.value ? moneyGBP(debtors.value.totalOutstanding) : null}
          figureLabel="outstanding"
          tone={debtors.value && debtors.value.overdueCount > 0 ? "amber" : "neutral"}
          error={debtors.error}
        />
        <BillingCard
          to="/office/ppm-payments"
          title="PPM payments"
          description="Upcoming PPM service visits and the payment state of any contract invoice the platform itself raised."
          figure={ppmPayments.value ? String(ppmPayments.value.outstanding) : null}
          figureLabel="not yet invoiced"
          tone={ppmPayments.value && ppmPayments.value.outstanding > 0 ? "amber" : "neutral"}
          error={ppmPayments.error}
        />
        <BillingCard
          to="/office/allowances"
          title="Allowances"
          description="Inclusive parts and call-outs each active contract has used this year, flagged when over so the excess can be billed."
          figure={allowances.value ? String(allowances.value.filter((a) => a.overLimit).length) : null}
          figureLabel="over limit"
          tone={allowances.value && allowances.value.some((a) => a.overLimit) ? "amber" : "neutral"}
          error={allowances.error}
        />
        {isAdmin && (
          <Link
            to="/office/timesheets"
            className="block overflow-hidden rounded-ele border border-ele-purple-accent transition hover:border-ele-green"
          >
            <div className="flex items-baseline justify-between bg-ele-inset-strong px-4 py-3 text-ele-strong">
              <span className="font-heading text-lg tracking-wide">Timesheets</span>
            </div>
            <p className="px-4 py-3 text-xs text-ele-body">
              Weekly hours and pay per engineer, and the Xero pay journal (admin only).
            </p>
          </Link>
        )}
        <BillingCard
          to="/office/contracts?filter=xero-billing"
          title="Xero billing check"
          description={
            xeroContent
              ? xeroContent.hint
              : "Whether each active contract's Xero repeating invoice still matches what it is meant to bill. Checked per contract on its own page; nothing here writes to Xero."
          }
          figure={xeroFigure}
          figureLabel={xeroContent && xeroContent.value !== "Not checked" ? "out of step" : ""}
          tone={xeroTone}
          error={dashboardTiles.error}
        />
      </div>
    </div>
  );
}
